

import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { SEARCH_URLS } from '../config/urls';

const ProcessingDeliveryModal = ({ onClose, onComplete, selectedProduct }) => {
  const history = useHistory();
  const [currentStep, setCurrentStep] = useState('processing');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [otherDetails, setOtherDetails] = useState({
    radiometricProcessing: '',
    geometricProcessing: ''
  });
  const [productDetails, setProductDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingLevels, setProcessingLevels] = useState([]);
  console.log(selectedProduct)
  // Add a state to track delivery options - moved to component level
  const [deliveryOptions, setDeliveryOptions] = useState({
    coordinate_system: '',
    bit_depth: '',
    spectral_bands: '',
    file_format: '',
    image_extent: 'Full Tile'
  });

  // Fetch product details from the API
  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        setLoading(true);
        // Use a default product ID if none is provided
        const token = localStorage.getItem('token');
        const productId = selectedProduct || 'Sentinel-2';
        const response = await fetch(SEARCH_URLS.getProductDetails(productId), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch product details');
        }
        
        const data = await response.json();
        console.log(data)
        setProductDetails(data);
        
        // Process the levels from API response
        const levels = [];
        if (data.processing_levels && data.processing_levels.Archival && data.processing_levels.Archival.processing_levels) {
          Object.entries(data.processing_levels.Archival.processing_levels).forEach(([key, value]) => {
            // Parse the description string into an array
            const descriptionItems = value.Description ? value.Description.split(',') : [];
            
            levels.push({
              value: key,
              label: key.toUpperCase(),
              details: descriptionItems
            });
          });
        }
        
        setProcessingLevels(levels);
        
        // Set the first level as selected if available
        if (levels.length > 0) {
          setSelectedLevel(levels[0].value);
          
          // Initialize other details if available
          const firstLevel = Object.values(data.processing_levels.Archival.processing_levels)[0];
          if (firstLevel) {
            const radiometricKeys = Object.keys(firstLevel.radiometric_processing_level || {});
            const geometricKeys = Object.keys(firstLevel.Geometric_Processing_levels || {});
            
            setOtherDetails({
              radiometricProcessing: radiometricKeys.length > 0 ? radiometricKeys[0] : '',
              geometricProcessing: geometricKeys.length > 0 ? geometricKeys[0] : ''
            });
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching product details:', error);
        setLoading(false);
      }
    };
    
    fetchProductDetails();
  }, [selectedProduct]);
  
  // Update delivery options when product details change
  useEffect(() => {
    if (productDetails && !loading && selectedLevel) {
      const levelDetails = productDetails?.processing_levels?.Archival?.processing_levels[selectedLevel] || {};
      
      setDeliveryOptions({
        coordinate_system: levelDetails.Coordinate_reference_system || 'Geographic-WGS84',
        bit_depth: levelDetails.Bit_Depth ? `${levelDetails.Bit_Depth}-bit` : '16-bit',
        spectral_bands: levelDetails.Band_combination ? 
          Object.keys(levelDetails.Band_combination)[0] + ' bands' : 
          '3-band: True Color (RGB)',
        file_format: levelDetails.file_format ? levelDetails.file_format[0] : 'Geotiff',
        image_extent: 'Full Tile'
      });
    }
  }, [productDetails, selectedLevel, loading]);

  const handleProcessingNext = () => {
    setCurrentStep('delivery');
  };

  // Handle option changes - moved to component level
  const handleOptionChange = (option, value) => {
    setDeliveryOptions(prev => ({
      ...prev,
      [option]: value
    }));
  };

  const handleDeliveryComplete = () => {
    // Collect all processing details
    const processingDetails = {
      level: selectedLevel,
      radiometric_processing: otherDetails.radiometricProcessing,
      geometric_processing: otherDetails.geometricProcessing,
      delivery_details: {
        ...deliveryOptions
      }
    };

    // Call onComplete with processing details
    onComplete(processingDetails);
  };

  const renderProcessingStep = () => {
    if (loading) {
      return (
        <div style={{ padding: '16px', textAlign: 'center' }}>
          <p>Loading processing options...</p>
        </div>
      );
    }

    return (
      <>
        <div style={{
          padding: '16px',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <h2 style={{ 
            margin: 0, 
            fontSize: '18px', 
            color: '#333',
            fontWeight: '500'
          }}>
            Processing Details
          </h2>
          <p style={{ 
            margin: '8px 0 0', 
            fontSize: '14px', 
            color: '#666'
          }}>
            Please select the level of data you want
          </p>
        </div>

        <div style={{ padding: '16px' }}>
          {processingLevels.map((level) => (
            <div key={level.value} style={{ 
              marginBottom: '8px'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center' 
              }}>
                <input 
                  type="radio" 
                  id={level.value} 
                  name="processingLevel" 
                  value={level.value}
                  checked={selectedLevel === level.value}
                  onChange={() => setSelectedLevel(level.value)}
                  style={{ 
                    marginRight: '12px', 
                    accentColor: '#1A73E8',
                    width: '18px',
                    height: '18px'
                  }} 
                />
                <label 
                  htmlFor={level.value} 
                  style={{ 
                    flex: 1,
                    color: '#333',
                    fontSize: '14px'
                  }}
                >
                  {level.label}
                </label>
              </div>
              
              {/* Details Section for the selected level */}
              {selectedLevel === level.value && (
                <div style={{
                  marginLeft: '30px',
                  marginTop: '8px',
                  color: '#666',
                  fontSize: '12px'
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                    Details
                  </div>
                  {level.details.map((detail, index) => (
                    <div key={index} style={{ marginBottom: '4px' }}>
                      {detail}
                    </div>
                  ))}
                </div>
              )}

              {/* Custom options for Other level - keeping this part for backward compatibility */}
              {selectedLevel === 'Other' && level.value === 'Other' && (
                <div style={{
                  marginLeft: '30px',
                  marginTop: '8px'
                }}>
                  <div style={{
                    marginBottom: '8px'
                  }}>
                    <div style={{
                      color: '#666',
                      fontSize: '12px',
                      marginBottom: '4px'
                    }}>
                      Radiometric processing level
                    </div>
                    <select 
                      value={otherDetails.radiometricProcessing}
                      onChange={(e) => setOtherDetails(prev => ({
                        ...prev, 
                        radiometricProcessing: e.target.value
                      }))}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    >
                      <option value="TOA">TOA</option>
                      <option value="BOA">BOA</option>
                    </select>
                  </div>
                  <div>
                    <div style={{
                      color: '#666',
                      fontSize: '12px',
                      marginBottom: '4px'
                    }}>
                      Geometric processing
                    </div>
                    <select 
                      value={otherDetails.geometricProcessing}
                      onChange={(e) => setOtherDetails(prev => ({
                        ...prev, 
                        geometricProcessing: e.target.value
                      }))}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    >
                      <option value="Georectified">Georectified</option>
                      <option value="Orthorectified">Orthorectified</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          padding: '16px',
          borderTop: '1px solid #e0e0e0'
        }}>
          <button 
            onClick={onClose}
            style={{
              marginRight: '12px',
              backgroundColor: 'transparent',
              color: '#1A73E8',
              border: 'none',
              borderRadius: '4px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button 
            onClick={handleProcessingNext}
            style={{
              backgroundColor: '#1A73E8',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Next
          </button>
        </div>
      </>
    );
  };

  const renderDeliveryStep = () => {
    if (loading) {
      return (
        <div style={{ padding: '16px', textAlign: 'center' }}>
          <p>Loading delivery options...</p>
        </div>
      );
    }

    // Get the selected level details
    const levelDetails = productDetails?.processing_levels?.Archival?.processing_levels[selectedLevel] || {};
    
    // Prepare options for the dropdowns from API response
    const crsOptions = levelDetails.Coordinate_reference_system 
      ? [levelDetails.Coordinate_reference_system] 
      : ['Geographic-WGS84'];
      
    const bitDepthOptions = levelDetails.Bit_Depth 
      ? [`${levelDetails.Bit_Depth}-bit`] 
      : ['16-bit', '11-bit'];
      
    const fileFormatOptions = levelDetails.file_format || ['Geotiff'];
    
    // For spectral bands, prepare options based on Band_combination
    let spectralBandOptions = ['3-band: True Color (RGB)', '4-band Multispectral'];
    if (levelDetails.Band_combination) {
      spectralBandOptions = Object.entries(levelDetails.Band_combination).map(([resolution, bands]) => {
        // Truncate very long band descriptions
        const displayBands = bands.length > 50 
          ? bands.substring(0, 50) + '...' 
          : bands;
        return `${resolution}: ${displayBands}`;
      });
    }

    return (
      <>
        <div style={{
          padding: '16px',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <h2 style={{ 
            margin: 0, 
            fontSize: '18px', 
            color: '#333',
            fontWeight: '500'
          }}>
            Delivery Details
          </h2>
        </div>

        <div style={{ padding: '16px' }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              color: '#666',
              fontSize: '12px',
              marginBottom: '4px',
              display: 'block'
            }}>
              Coordinate reference system
            </label>
            <select 
              value={deliveryOptions.coordinate_system}
              onChange={(e) => handleOptionChange('coordinate_system', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              {crsOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{
              color: '#666',
              fontSize: '12px',
              marginBottom: '4px',
              display: 'block'
            }}>
              Bit depth per pixel
            </label>
            <select 
              value={deliveryOptions.bit_depth}
              onChange={(e) => handleOptionChange('bit_depth', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              {bitDepthOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{
              color: '#666',
              fontSize: '12px',
              marginBottom: '4px',
              display: 'block'
            }}>
              Spectral bands combination
            </label>
            <select 
              value={deliveryOptions.spectral_bands}
              onChange={(e) => handleOptionChange('spectral_bands', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              {spectralBandOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{
              color: '#666',
              fontSize: '12px',
              marginBottom: '4px',
              display: 'block'
            }}>
              File format
            </label>
            <select 
              value={deliveryOptions.file_format}
              onChange={(e) => handleOptionChange('file_format', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              {fileFormatOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{
              color: '#666',
              fontSize: '12px',
              marginBottom: '4px',
              display: 'block'
            }}>
              Image Extent
            </label>
            <select 
              value={deliveryOptions.image_extent}
              onChange={(e) => handleOptionChange('image_extent', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              <option value="Full Tile">Full Tile</option>
              {levelDetails.Clippable === "False" && 
                <option value="Full scene">Full scene</option>
              }
            </select>
          </div>
        </div>

        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          padding: '16px',
          borderTop: '1px solid #e0e0e0'
        }}>
          <button 
            onClick={() => setCurrentStep('processing')}
            style={{
              marginRight: '12px',
              backgroundColor: 'transparent',
              color: '#1A73E8',
              border: 'none',
              borderRadius: '4px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Back
          </button>
          <button 
            onClick={handleDeliveryComplete}
            style={{
              backgroundColor: '#1A73E8',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Complete
          </button>
        </div>
      </>
    );
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        width: '400px',
        maxWidth: '90%',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
      }}>
        {currentStep === 'processing' ? renderProcessingStep() : renderDeliveryStep()}
      </div>
    </div>
  );
};

export default ProcessingDeliveryModal;